import type { MouseEvent, ReactNode } from "react";
import { ArrowRightIcon, PlayIcon } from "../icons";

/**
 * The website's buttons — every one of them a link, because every one of them
 * goes somewhere (Connect, a game page, the free round).
 *
 * 🔴 A PILL WITH A LIP — the offset shadow the kid buttons carry
 * (`design-system.md` §5's `0 8px 0 0 primary-deep`), scaled down for an adult
 * page. Hover lifts it a pixel; pressing sinks it into the lip, which is the
 * physical "press" a four-year-old's button has and the reason the website
 * feels like the same product.
 *
 * ⚠️ FOUR VARIANTS, AND ONLY ONE IS PINK. `primary` is the page's conversion
 * (book a demo); `glass` sits on imagery; `sun` is "play a free game"; `outline`
 * is a quiet third choice on a light ground.
 *
 * ⚠️ THE FOCUS RING IS `ink`, NEVER `primary`. A pink ring around a pink button
 * is three warm tones stacked — present, and practically invisible.
 */
export type CtaVariant = "primary" | "glass" | "sun" | "outline";

const VARIANT: Record<CtaVariant, string> = {
  primary:
    "bg-primary text-primary-ink shadow-[0_6px_0_var(--color-primary-deep)] hover:bg-primary-bright active:shadow-[0_2px_0_var(--color-primary-deep)]",
  glass:
    "bg-surface/95 text-ink shadow-[0_6px_0_color-mix(in_srgb,var(--color-indigo)_35%,transparent)] hover:bg-surface active:shadow-[0_2px_0_color-mix(in_srgb,var(--color-indigo)_35%,transparent)]",
  sun: "bg-sunshine text-ink shadow-[0_6px_0_var(--color-kid-voice-shadow)] hover:brightness-105 active:shadow-[0_2px_0_var(--color-kid-voice-shadow)]",
  outline:
    "border-2 border-line bg-surface text-ink shadow-[0_4px_0_var(--color-line)] hover:border-primary hover:text-primary active:shadow-[0_1px_0_var(--color-line)]",
};

const SIZE = {
  md: "h-12 gap-2 px-6 text-dash-sm",
  lg: "h-14 gap-2.5 px-7 text-dash-md sm:h-16 sm:px-8",
} as const;

export interface CtaLinkProps {
  href: string;
  variant: CtaVariant;
  size?: keyof typeof SIZE;
  /** A trailing arrow (the default) or a leading play disc. */
  icon?: "arrow" | "play" | "none";
  className?: string;
  children: ReactNode;
  /**
   * For a link to a section of the same page: scroll there instead of writing
   * the anchor to `location.hash` (which the hash router would treat as a route).
   */
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  "data-testid"?: string;
}

export function CtaLink({
  href,
  variant,
  size = "lg",
  icon = "arrow",
  className = "",
  children,
  onClick,
  "data-testid": testId,
}: CtaLinkProps) {
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      className={`group inline-flex shrink-0 items-center justify-center rounded-pill font-dash font-extrabold whitespace-nowrap outline-none transition duration-150 ease-dash hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 active:translate-y-1 ${VARIANT[variant]} ${SIZE[size]} ${
        icon === "play" ? "pl-2.5 sm:pl-3" : ""
      } ${className}`}
    >
      {icon === "play" ? (
        <span
          aria-hidden="true"
          className={`flex shrink-0 items-center justify-center rounded-pill ${
            size === "lg" ? "h-9 w-9 sm:h-10 sm:w-10" : "h-8 w-8"
          } ${variant === "sun" ? "bg-ink/10 text-ink" : "bg-primary text-primary-ink"}`}
        >
          <PlayIcon className="h-4 w-4 translate-x-px" />
        </span>
      ) : null}
      {children}
      {icon === "arrow" ? (
        <ArrowRightIcon className="h-5 w-5 transition-transform duration-200 ease-dash group-hover:translate-x-1" />
      ) : null}
    </a>
  );
}
